import React, { useMemo, useState } from 'react';
import {
  Box,
  Chip,
  IconButton,
  styled,
  Switch,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  Typography,
  useTheme
} from '@mui/material';
import { ColumnDef } from '@tanstack/react-table';
import { MdOutlineEdit } from 'react-icons/md';
import { RiDeleteBin6Line, RiRefreshLine } from 'react-icons/ri';
import { Link, useNavigate } from 'react-router-dom';
import Button from 'components/Common/Button';
import DataTable from 'components/Common/DataTable';
import Pagination from 'components/Common/Pagination';
import { formatDate } from 'utils/helperFunctions';
import { DatasetResponse } from 'apis/business/setting/knowledgeBase/types';
import { FaPlus } from 'react-icons/fa6';
import { RxCrossCircled } from 'react-icons/rx';
import {
  useDeleteAgentDataSet,
  useGetAgent,
  useGetAgentDataSets,
  useUpdateAgentDataSet
} from 'apis/business/agent/hook';
import * as urls from 'routes/config';
import { useBusiness } from 'providers/context/BusinessProvider';
import { useCancelDataSet, useRestartDataSet } from 'apis/business/setting/knowledgeBase/hook';

export default function AgentDatasets({ type }: { type: 'website' | 'document' }) {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const { data: agent } = useGetAgent();

  const filter =
    type === 'website' ? { 'filter[datasource_type_eq]': 1 } : { 'filter[datasource_type_not_in][]': [1, 8] };
  const { data: dataSets, isLoading: isDataSetsLoading } = useGetAgentDataSets({
    ...filter,
    page: currentPage
  });
  const { mutate: deleteDataSet } = useDeleteAgentDataSet();
  const { mutateAsync: handleDataSet } = useUpdateAgentDataSet();
  const { mutate: cancelDataSet } = useCancelDataSet(agent?.default_kb_id);
  const { mutate: restartDataSet } = useRestartDataSet(agent?.default_kb_id);

  const columns = useColumns(deleteDataSet, handleDataSet, type, cancelDataSet, restartDataSet);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px'
        }}
      >
        <Typography variant='h6'>{type === 'website' ? 'Web URL Datasets' : 'Documents Dataset'}</Typography>

        <Button onClick={() => navigate(type === 'website' ? 'add-website' : 'add-document')}>
          <Box display='flex' alignItems='center' gap={1}>
            <FaPlus /> Add
          </Box>
        </Button>
      </Box>
      <DataTable data={dataSets?.data || []} columns={columns} isHeaderSticky={true} isLoading={isDataSetsLoading} />
      <Pagination totalPages={dataSets?.meta?.total_pages} currentPage={currentPage} onChange={setCurrentPage} />
    </>
  );
}

type DeleteFn = (id: string) => void;
type UpdateFn = (payload: any) => void;
type CancelFn = (id: string) => void;

const pillColor = {
  parsing: 'warning',
  success: 'success',
  failed: 'error',
  cancelled: 'error'
};

function useColumns(
  deleteDataSet: DeleteFn,
  handleDataSet: UpdateFn,
  type: 'website' | 'document',
  cancelDataSet: CancelFn,
  restartDataSet: CancelFn
) {
  const theme = useTheme();
  const { data: agent } = useGetAgent();
  const { selectedWorkspace } = useBusiness();

  return useMemo<ColumnDef<DatasetResponse>[]>(
    () => [
      {
        header: 'NAME',
        accessorKey: 'name',
        cell: (info) => (
          <Link
            to={`${urls.business.base}/${urls.business.KnowledgeBases(selectedWorkspace!)}/${agent?.default_kb_id}/dataset/${urls.knowledgeBase.chunks(info.row.original.id, info.row.original.name ?? 'chunks')}`}
            style={{ color: theme.palette.primary.main, fontWeight: 500 }}
          >
            {info.getValue() as string}
          </Link>
        )
      },
      {
        header: 'CHUNK NO.',
        accessorKey: 'chunks_count'
      },
      {
        header: 'UPLOAD DATE',
        accessorKey: 'upload_date',
        cell: (info) => <Typography>{formatDate(info.row.original.created_at)}</Typography>
      },
      {
        header: 'CHUNK METHOD',
        accessorKey: 'datasource_type',
        cell: (info) => renderChunkMethod[info.row.original.datasource_type]
      },
      {
        header: 'ENABLE',
        accessorKey: 'enable',
        cell: (info) => (
          <Switch
            defaultChecked={Boolean(info.getValue())}
            onChange={(e) =>
              handleDataSet({
                datasetId: info.row.original.id,
                payload: { dataset: { enable: e.target.checked } }
              })
            }
          />
        )
      },
      {
        header: 'STATUS',
        cell: (info) => (
          <Box display='flex' gap={1}>
            <HtmlTooltip
              title={
                <>
                  <Box>
                    <Typography variant='body2'>
                      <Typography component='span' variant='body2' fontWeight={600}>
                        Progress Begin At:
                      </Typography>
                      {formatDate(info.row.original.created_at)}
                    </Typography>
                    <Typography variant='body2' my={1}>
                      <Typography component='span' variant='body2' fontWeight={600}>
                        Progress:
                      </Typography>
                      {info.row.original.progress <= 0 ? '0' : (Number(info.row.original.progress) * 100).toFixed(2)}%
                    </Typography>
                    <Box display='flex' gap={1}>
                      <Typography component='span' variant='body2' fontWeight={600}>
                        Progress Msg:
                      </Typography>
                      <Typography variant='body2'>
                        {info.row.original.progress_message?.split('\n')?.map((line, index) => (
                          <React.Fragment key={index}>
                            {line}
                            <br />
                          </React.Fragment>
                        ))}
                      </Typography>
                    </Box>
                  </Box>
                </>
              }
            >
              <Box display='flex' alignItems='center' gap={1}>
                <Chip
                  label={info.row.original.status}
                  variant='outlined'
                  color={pillColor[info.row.original.status || 'failed'] as 'success' | 'warning' | 'error'}
                  sx={{ cursor: 'help' }}
                />
                {info.row.original.status === 'parsing' && (
                  <IconButton color='error' onClick={() => cancelDataSet(String(info.row.original.id))}>
                    <RxCrossCircled />
                  </IconButton>
                )}
                {['cancelled', 'failed'].includes(info.row.original.status) && (
                  <IconButton onClick={() => restartDataSet(String(info.row.original.id))}>
                    <RiRefreshLine />
                  </IconButton>
                )}
              </Box>
            </HtmlTooltip>
          </Box>
        )
      },
      {
        header: 'Action',
        cell: (info) => (
          <Box display='flex' alignItems='center'>
            <Link to={`${info.row.original.id}/${type === 'website' ? 'edit-website' : 'edit-document'}`}>
              <IconButton>
                <MdOutlineEdit />
              </IconButton>
            </Link>
            <IconButton onClick={() => deleteDataSet(String(info.row.original.id))} color='error'>
              <RiDeleteBin6Line style={{ color: 'red' }} />
            </IconButton>
          </Box>
        )
      }
    ],
    [
      deleteDataSet,
      handleDataSet,
      cancelDataSet,
      restartDataSet,
      type,
      agent,
      selectedWorkspace,
      theme.palette.primary.main
    ]
  );
}

const renderChunkMethod = {
  naive: 'General',
  _table: 'Table',
  website: 'Website',
  book: 'Book',
  laws: 'Laws',
  manual: 'Manual',
  one: 'One',
  paper: 'Paper',
  presentation: 'Presentation',
  qa: 'Q&A',
  resume: 'Resume'
};

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip placement='top' arrow {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#f5f5f9',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 520,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #dadde9'
  }
}));
